import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { savePhoto } from './photoDB';
import { MAX_PHOTOS } from './limits';
import './App.css';

function LicensePlatePhotography() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Passed from UploadFrame when photographing a custom frame (not a plate).
  // Threaded through to PhotoGallery → LicencePlateConfirm so the confirm
  // screen knows to continue to /upload-plates after a successful frame submit.
  const assetType = location.state?.assetType || 'plate';

  const [photoKeys, setPhotoKeys] = useState([]);
  const [flash, setFlash] = useState(false);
  const [isLandscape, setIsLandscape] = useState(true);
  const [levelStatus, setLevelStatus] = useState('level');
  const [cameraStarted, setCameraStarted] = useState(false);
  const [saveError, setSaveError] = useState('');

  // ── Orientation (portrait/landscape) detection ──────────────────────────
  useEffect(() => {
    const checkOrientation = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  // ── Camera start / stop ──────────────────────────────────────────────────
  const startCamera = async () => {
    try {
      const constraints = {
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) {
      console.error('Error accessing camera', err);
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
    }
  };

  useEffect(() => {
    if (isLandscape && cameraStarted) {
      startCamera();
    } else {
      stopCamera();
    }
    return stopCamera;
  }, [isLandscape, cameraStarted]);

  // ── Device level indicator — with iOS permission handling ────────────────
  // Mirrors the same pattern used in Photography.js.
  useEffect(() => {
    let permissionGranted = false;

    const handleOrientation = (event) => {
      if (
        !permissionGranted &&
        typeof DeviceOrientationEvent !== 'undefined' &&
        typeof DeviceOrientationEvent.requestPermission === 'function'
      ) {
        return;
      }
      const { beta, gamma } = event;
      const isBetaLevel = Math.abs(beta - 0) < 5;
      const isGammaLevel = Math.abs(gamma - (-90)) < 5;
      setLevelStatus(isBetaLevel && isGammaLevel ? 'level' : 'not-level');
    };

    async function requestOrientationPermission() {
      if (
        typeof DeviceOrientationEvent !== 'undefined' &&
        typeof DeviceOrientationEvent.requestPermission === 'function'
      ) {
        try {
          const response = await DeviceOrientationEvent.requestPermission();
          if (response === 'granted') {
            permissionGranted = true;
            window.addEventListener('deviceorientation', handleOrientation, true);
          }
        } catch (e) {
          console.warn('Device orientation permission denied:', e);
        }
      } else {
        window.addEventListener('deviceorientation', handleOrientation, true);
      }
    }

    // Declared at effect scope so the cleanup removes the SAME reference
    // that was added (see Photography.js - removeEventListener with a
    // different function is a silent no-op).
    const requestOnUserGesture = () => {
      requestOrientationPermission();
      window.removeEventListener('touchend', requestOnUserGesture);
      window.removeEventListener('click', requestOnUserGesture);
    };

    if (
      typeof DeviceOrientationEvent !== 'undefined' &&
      typeof DeviceOrientationEvent.requestPermission === 'function'
    ) {
      window.addEventListener('touchend', requestOnUserGesture);
      window.addEventListener('click', requestOnUserGesture);
    } else {
      window.addEventListener('deviceorientation', handleOrientation, true);
    }

    return () => {
      window.removeEventListener('deviceorientation', handleOrientation, true);
      window.removeEventListener('touchend', requestOnUserGesture);
      window.removeEventListener('click', requestOnUserGesture);
    };
  }, []);

  // ── Photo capture ─────────────────────────────────────────────────────────
  const handleStartCamera = () => setCameraStarted(true);

  const takePhoto = async () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    const context = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const photoData = canvas.toDataURL('image/jpeg', 0.92);
    try {
      const key = await savePhoto(photoData);
      setPhotoKeys(prev => [...prev, key]);
      setSaveError('');
      setFlash(true);
      setTimeout(() => setFlash(false), 200);
    } catch (err) {
      console.error('Failed to save photo:', err);
      setSaveError('This photo could not be saved - your device storage may be full. Free up space and try again.');
    }
  };

  const goToGallery = () => {
    navigate('/photo-gallery', {
      state: { photoKeys, source: 'license-plate', assetType },
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (!isLandscape) {
    return (
      <div className="rotate-device">
        <p>Please rotate your device to landscape to capture license plates.</p>
      </div>
    );
  }

  if (!cameraStarted) {
    return (
      <div className="start-camera-overlay">
        <button className="App-button" onClick={handleStartCamera}>
          Start Camera
        </button>
        <p>Tap to enable camera for license plate photos.</p>
      </div>
    );
  }

  return (
    <div className="Photography">
      <div className="Photography-layout">
        <div className="Photography-box-area">
          <div className="Photography-box">
            <div className={`level-indicator ${levelStatus}`}>
              {levelStatus === 'level' ? '🟢 Level' : '🔴 Not Level'}
            </div>
            <div className="plate-framing-overlay" />
            <video ref={videoRef} autoPlay playsInline className="Photography-video" />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            {flash && <div className="flash-overlay"></div>}
            {saveError && (
              <div style={{ position: 'absolute', bottom: 12, left: 12, right: 12, zIndex: 4, background: 'rgba(180,0,0,0.85)', color: 'white', padding: '8px 12px', borderRadius: '8px' }}>
                {saveError}
              </div>
            )}
          </div>
        </div>

        <div className="Photography-controls-area">
          {/* Live shot counter */}
          <div className="photo-counter">
            📷 {photoKeys.length} / ∞
            {photoKeys.length > 0 && (
              <span className="photo-counter-sub">select up to {MAX_PHOTOS} to submit</span>
            )}
          </div>
          <div className="Photography-controls">
            <button className="Photography-button take-photo" onClick={takePhoto}>
              Take Photo
            </button>
            <button className="Photography-button view-gallery" onClick={goToGallery}>
              View Plate Photos
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LicensePlatePhotography;
